import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Plus,
  UserCog,
  ShieldCheck,
  ShieldOff,
  Users2,
  MoreVertical,
  Pencil,
  Key,
  Ban,
  Play,
  Copy,
  Check,
  RefreshCw,
  Download,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { formatDateTime, formatRelative } from '@/lib/format';

interface SuperAdminUser {
  id: string;
  email: string;
  full_name: string;
  active: boolean;
  last_login_at: string | null;
  password_changed_at: string | null;
  totp_enabled: boolean;
  created_at: string;
}

interface TotpSetupResponse {
  secret: string;
  otpauth_url: string;
  qr_data_url: string;
}

interface TotpConfirmResponse {
  totp_enabled: boolean;
  totp_verified_at: string;
  recovery_codes: string[];
}

interface TotpRegenerateResponse {
  recovery_codes: string[];
}

interface InviteResponse extends SuperAdminUser {
  temp_password?: string;
}

interface ListResponse {
  items: SuperAdminUser[];
}

const InviteSchema = z.object({
  full_name: z.string().trim().min(2, 'Full name required').max(120),
  email: z.string().email('Invalid email').max(254)
});
type InviteForm = z.infer<typeof InviteSchema>;

const EditSchema = z.object({
  full_name: z.string().trim().min(2, 'Full name required').max(120)
});
type EditForm = z.infer<typeof EditSchema>;

const PasswordSchema = z
  .object({
    new_password: z.string().min(12, 'At least 12 characters'),
    confirm_password: z.string()
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match'
  })
  .refine((v) => /[A-Z]/.test(v.new_password), {
    path: ['new_password'],
    message: 'Include at least one uppercase letter'
  })
  .refine((v) => /[0-9]/.test(v.new_password), {
    path: ['new_password'],
    message: 'Include at least one digit'
  })
  .refine((v) => /[^A-Za-z0-9]/.test(v.new_password), {
    path: ['new_password'],
    message: 'Include at least one special character'
  });
type PasswordForm = z.infer<typeof PasswordSchema>;

export function SuperAdminUsersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const myUserId = useAuthStore((s) => s.user?.id);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [tempPasswordView, setTempPasswordView] = useState<{ email: string; password: string } | null>(null);

  const list = useQuery<ListResponse>({
    queryKey: ['superadmin', 'users'],
    queryFn: () => get<ListResponse>('/superadmin/users')
  });

  const items: SuperAdminUser[] = useMemo(() => list.data?.items ?? [], [list.data]);
  const totalCount = items.length;
  const activeCount = items.filter((u) => u.active).length;
  const inactiveCount = totalCount - activeCount;

  const inviteForm = useForm<InviteForm>({
    resolver: zodResolver(InviteSchema),
    defaultValues: { full_name: '', email: '' }
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
  };

  const inviteMutation = useMutation({
    mutationFn: (values: InviteForm) =>
      post<InviteResponse, InviteForm>('/superadmin/users', values),
    onSuccess: (res) => {
      toast.success(`Invited ${res.full_name}`);
      invalidate();
      setInviteOpen(false);
      inviteForm.reset();
      if (res.temp_password) {
        setTempPasswordView({ email: res.email, password: res.temp_password });
      }
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const inviteDialog = (
    <Dialog
      open={inviteOpen}
      onOpenChange={(o) => {
        setInviteOpen(o);
        if (!o) inviteForm.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4" /> Invite admin
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite super-admin</DialogTitle>
          <DialogDescription>
            Creates a new operator account. A one-time temporary password is generated server-side and shown ONCE — copy it and share it via a trusted channel.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={inviteForm.handleSubmit((v) => inviteMutation.mutate(v))}
          className="flex flex-col gap-vt-4"
        >
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="invite-full-name">Full name</Label>
            <Input id="invite-full-name" autoFocus {...inviteForm.register('full_name')} />
            {inviteForm.formState.errors.full_name ? (
              <p className="text-vt-12 text-vt-alert" role="alert">
                {inviteForm.formState.errors.full_name.message}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="invite-email">Email</Label>
            <Input id="invite-email" type="email" {...inviteForm.register('email')} />
            {inviteForm.formState.errors.email ? (
              <p className="text-vt-12 text-vt-alert" role="alert">
                {inviteForm.formState.errors.email.message}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={inviteMutation.isPending} loadingText="Inviting…">
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <PageHeader
        title="Super-admin team"
        description="HoodDesk staff with cross-tenant access. Manage operator accounts, rotate passwords, and disable lost access."
        actions={inviteDialog}
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Users2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Total operators</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-10" /> : totalCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Active</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-10" /> : activeCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]">
                <ShieldOff className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Inactive</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-10" /> : inactiveCount}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {list.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <EmptyState
                icon={<UserCog className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load operators"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : items.length === 0 ? (
              <EmptyState
                icon={<Users2 className="h-6 w-6" strokeWidth={1.75} />}
                title="No operators yet"
                description="Invite your first super-admin to share cross-tenant access."
                action={
                  <Button onClick={() => setInviteOpen(true)}>
                    <Plus className="h-4 w-4" /> Invite admin
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      isSelf={myUserId === u.id}
                      onMutated={invalidate}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Temp password reveal — modal sits OUTSIDE the invite dialog so closing
          the invite doesn't dismiss the only place the plaintext is visible. */}
      <TempPasswordDialog
        view={tempPasswordView}
        onClose={() => setTempPasswordView(null)}
      />
    </>
  );
}

function UserRow({
  user: u,
  isSelf,
  onMutated
}: {
  user: SuperAdminUser;
  isSelf: boolean;
  onMutated: () => void;
}): JSX.Element {
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [totpSetupOpen, setTotpSetupOpen] = useState(false);
  const [totpDisableOpen, setTotpDisableOpen] = useState(false);
  const [totpRegenerateOpen, setTotpRegenerateOpen] = useState(false);

  const editForm = useForm<EditForm>({
    resolver: zodResolver(EditSchema),
    defaultValues: { full_name: u.full_name }
  });
  useEffect(() => {
    if (editOpen) editForm.reset({ full_name: u.full_name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen, u.id]);

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(PasswordSchema),
    defaultValues: { new_password: '', confirm_password: '' }
  });

  const editMutation = useMutation({
    mutationFn: (values: EditForm) =>
      patch<SuperAdminUser>(`/superadmin/users/${u.id}`, { full_name: values.full_name }),
    onSuccess: () => {
      toast.success(`Updated ${u.full_name}`);
      setEditOpen(false);
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const passwordMutation = useMutation({
    mutationFn: (values: PasswordForm) =>
      post<SuperAdminUser>(`/superadmin/users/${u.id}/change-password`, {
        new_password: values.new_password
      }),
    onSuccess: () => {
      toast.success(`Password updated for ${u.full_name}`);
      setPasswordOpen(false);
      passwordForm.reset();
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deactivateMutation = useMutation({
    mutationFn: () => post<SuperAdminUser>(`/superadmin/users/${u.id}/deactivate`, {}),
    onSuccess: () => {
      toast.success(`Deactivated ${u.full_name}`);
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const reactivateMutation = useMutation({
    mutationFn: () => post<SuperAdminUser>(`/superadmin/users/${u.id}/reactivate`, {}),
    onSuccess: () => {
      toast.success(`Reactivated ${u.full_name}`);
      onMutated();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const copyEmail = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(u.email);
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 1500);
    } catch {
      toast.error('Could not copy email');
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-vt-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
              {u.full_name
                .split(' ')
                .map((w) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-vt-2">
                <span className="truncate font-medium text-vt-text">
                  {u.full_name}
                </span>
                {isSelf ? <span className="text-vt-12 text-vt-text-soft">(you)</span> : null}
                {u.totp_enabled ? (
                  <Badge variant="ok" aria-label="Two-factor authentication enabled">
                    <ShieldCheck className="h-3 w-3" strokeWidth={2} /> 2FA
                  </Badge>
                ) : null}
              </div>
              <div className="truncate font-mono text-vt-12 text-vt-text-soft">
                {u.id.slice(0, 8)}…
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-vt-text-muted">{u.email}</TableCell>
        <TableCell>
          <Badge variant={u.active ? 'ok' : 'muted'}>{u.active ? 'Active' : 'Inactive'}</Badge>
        </TableCell>
        <TableCell className="text-vt-text-muted">{formatRelative(u.last_login_at)}</TableCell>
        <TableCell className="text-vt-text-muted">{formatDateTime(u.created_at)}</TableCell>
        <TableCell>
          <div className="flex items-center justify-end">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  aria-label="User actions"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors duration-vt hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={6}
                  className="z-50 min-w-52 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface p-1 shadow-vt-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                >
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                    onSelect={() => setEditOpen(true)}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit details
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                    onSelect={() => setPasswordOpen(true)}
                  >
                    <Key className="h-3.5 w-3.5" /> Change password
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                    onSelect={() => void copyEmail()}
                  >
                    {emailCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5" /> Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" /> Copy email
                      </>
                    )}
                  </DropdownMenu.Item>
                  {/* Two-factor controls — only the signed-in operator can manage
                      their own factor; one admin cannot enrol/disable another
                      admin's TOTP from this UI. */}
                  {isSelf && !u.totp_enabled ? (
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                      onSelect={() => setTotpSetupOpen(true)}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" /> Set up 2FA
                    </DropdownMenu.Item>
                  ) : null}
                  {isSelf && u.totp_enabled ? (
                    <>
                      <DropdownMenu.Item
                        className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                        onSelect={() => setTotpRegenerateOpen(true)}
                      >
                        <RefreshCw className="h-3.5 w-3.5" /> Regenerate recovery codes
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-alert outline-none data-[highlighted]:bg-vt-alert/10"
                        onSelect={() => setTotpDisableOpen(true)}
                      >
                        <ShieldOff className="h-3.5 w-3.5" /> Disable 2FA
                      </DropdownMenu.Item>
                    </>
                  ) : null}
                  <DropdownMenu.Separator className="my-1 h-px bg-vt-border" />
                  {u.active ? (
                    <DropdownMenu.Item
                      disabled={isSelf}
                      className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-alert outline-none data-[highlighted]:bg-vt-alert/10 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
                      onSelect={() => deactivateMutation.mutate()}
                    >
                      <Ban className="h-3.5 w-3.5" /> Deactivate
                    </DropdownMenu.Item>
                  ) : (
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-text outline-none data-[highlighted]:bg-vt-surface-2"
                      onSelect={() => reactivateMutation.mutate()}
                    >
                      <Play className="h-3.5 w-3.5" /> Reactivate
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </TableCell>
      </TableRow>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit operator</DialogTitle>
            <DialogDescription>
              Update the operator's display name. Email is immutable — invite a new account if it must change.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={editForm.handleSubmit((v) => editMutation.mutate(v))}
            className="flex flex-col gap-vt-4"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor={`edit-name-${u.id}`}>Full name</Label>
              <Input id={`edit-name-${u.id}`} {...editForm.register('full_name')} />
              {editForm.formState.errors.full_name ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {editForm.formState.errors.full_name.message}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={editMutation.isPending} loadingText="Saving…">
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog
        open={passwordOpen}
        onOpenChange={(o) => {
          setPasswordOpen(o);
          if (!o) passwordForm.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change password — {u.full_name}</DialogTitle>
            <DialogDescription>
              Minimum 12 characters with at least one uppercase letter, one digit, and one special character. The user will need to log in again with the new password.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={passwordForm.handleSubmit((v) => passwordMutation.mutate(v))}
            className="flex flex-col gap-vt-4"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor={`pw-new-${u.id}`}>New password</Label>
              <Input
                id={`pw-new-${u.id}`}
                type="password"
                autoComplete="new-password"
                {...passwordForm.register('new_password')}
              />
              {passwordForm.formState.errors.new_password ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {passwordForm.formState.errors.new_password.message}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor={`pw-confirm-${u.id}`}>Confirm password</Label>
              <Input
                id={`pw-confirm-${u.id}`}
                type="password"
                autoComplete="new-password"
                {...passwordForm.register('confirm_password')}
              />
              {passwordForm.formState.errors.confirm_password ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {passwordForm.formState.errors.confirm_password.message}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPasswordOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={passwordMutation.isPending}
                loadingText="Updating…"
              >
                Update password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 2FA setup / disable / regenerate — all gated to isSelf via the
          menu visibility above. Each dialog owns its own data fetch +
          mutation lifecycle so closing one cannot leak state into another. */}
      {isSelf && !u.totp_enabled ? (
        <TotpSetupDialog
          open={totpSetupOpen}
          onOpenChange={setTotpSetupOpen}
          onEnabled={onMutated}
        />
      ) : null}
      {isSelf && u.totp_enabled ? (
        <>
          <TotpDisableDialog
            open={totpDisableOpen}
            onOpenChange={setTotpDisableOpen}
            onDisabled={onMutated}
          />
          <TotpRegenerateDialog
            open={totpRegenerateOpen}
            onOpenChange={setTotpRegenerateOpen}
          />
        </>
      ) : null}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 2FA setup dialog — two-phase. Phase A fetches a fresh secret+QR on
// open; phase B shows the plaintext recovery codes after the user proves
// possession with a 6-digit code.
// ──────────────────────────────────────────────────────────────────────
const TotpConfirmSchema = z.object({
  code: z.string().regex(/^\d{6}$/, '6-digit code')
});
type TotpConfirmForm = z.infer<typeof TotpConfirmSchema>;

function TotpSetupDialog({
  open,
  onOpenChange,
  onEnabled
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onEnabled: () => void;
}): JSX.Element {
  const [phase, setPhase] = useState<'enrol' | 'codes'>('enrol');
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const form = useForm<TotpConfirmForm>({
    resolver: zodResolver(TotpConfirmSchema),
    defaultValues: { code: '' }
  });

  const setupMutation = useMutation({
    mutationFn: () => post<TotpSetupResponse>('/superadmin/users/me/totp/setup', {}),
    onSuccess: (res) => setSetup(res),
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const confirmMutation = useMutation({
    mutationFn: (values: TotpConfirmForm) => {
      if (!setup) throw new Error('Missing setup state');
      return post<TotpConfirmResponse, { secret: string; code: string }>(
        '/superadmin/users/me/totp/confirm',
        { secret: setup.secret, code: values.code }
      );
    },
    onSuccess: (res) => {
      setRecoveryCodes(res.recovery_codes);
      setPhase('codes');
      onEnabled();
      toast.success('Two-factor authentication enabled');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Reset state every time the dialog opens, and fetch a fresh secret.
  useEffect(() => {
    if (open) {
      setPhase('enrol');
      setSetup(null);
      setRecoveryCodes(null);
      setSecretCopied(false);
      form.reset({ code: '' });
      setupMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copySecret = async (): Promise<void> => {
    if (!setup?.secret) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setSecretCopied(true);
      window.setTimeout(() => setSecretCopied(false), 1500);
    } catch {
      toast.error('Could not copy secret');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set up two-factor authentication</DialogTitle>
          <DialogDescription>
            {phase === 'enrol'
              ? 'Scan the QR code with your authenticator app (Google Authenticator, 1Password, Authy, etc.), then enter the 6-digit code it generates.'
              : 'Save these recovery codes somewhere safe. Each code can be used once to sign in if you lose access to your authenticator. You will not be able to view them again.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'enrol' ? (
          <>
            {setupMutation.isPending || !setup ? (
              <div className="flex flex-col items-center justify-center gap-vt-3 py-vt-6">
                <Skeleton className="h-48 w-48" />
                <Skeleton className="h-4 w-40" />
              </div>
            ) : (
              <div className="flex flex-col gap-vt-4">
                <div className="flex justify-center">
                  <img
                    src={setup.qr_data_url}
                    alt="TOTP QR code"
                    className="h-48 w-48 rounded-vt-sm border border-vt-border bg-white p-2"
                  />
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label>Can't scan? Enter this secret manually</Label>
                  <div className="flex items-center gap-vt-2">
                    <code className="flex-1 select-all break-all rounded-vt-sm border border-vt-border bg-vt-surface-2 px-vt-3 py-vt-2 font-mono text-vt-12 text-vt-text">
                      {setup.secret}
                    </code>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void copySecret()}
                      aria-label="Copy secret"
                    >
                      {secretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <form
                  onSubmit={form.handleSubmit((v) => confirmMutation.mutate(v))}
                  className="flex flex-col gap-vt-2"
                >
                  <Label htmlFor="totp-setup-code">Enter the 6-digit code from your app</Label>
                  <Input
                    id="totp-setup-code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    className="text-center font-mono text-vt-24 tracking-[0.4em]"
                    {...form.register('code', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                        form.setValue('code', digits, { shouldValidate: digits.length === 6 });
                      }
                    })}
                  />
                  {form.formState.errors.code ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">
                      {form.formState.errors.code.message}
                    </p>
                  ) : null}
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" loading={confirmMutation.isPending} loadingText="Verifying…">
                      Verify and enable
                    </Button>
                  </DialogFooter>
                </form>
              </div>
            )}
          </>
        ) : (
          <RecoveryCodesPanel
            codes={recoveryCodes ?? []}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Disable 2FA — password-gated.
// ──────────────────────────────────────────────────────────────────────
const TotpPasswordSchema = z.object({
  password: z.string().min(8, 'Enter your password')
});
type TotpPasswordForm = z.infer<typeof TotpPasswordSchema>;

function TotpDisableDialog({
  open,
  onOpenChange,
  onDisabled
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onDisabled: () => void;
}): JSX.Element {
  const form = useForm<TotpPasswordForm>({
    resolver: zodResolver(TotpPasswordSchema),
    defaultValues: { password: '' }
  });

  useEffect(() => {
    if (open) form.reset({ password: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const disableMutation = useMutation({
    mutationFn: (values: TotpPasswordForm) =>
      post<{ totp_enabled: boolean }, TotpPasswordForm>(
        '/superadmin/users/me/totp/disable',
        values
      ),
    onSuccess: () => {
      toast.success('Two-factor authentication disabled');
      onDisabled();
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disable two-factor authentication</DialogTitle>
          <DialogDescription>
            Confirm your password to remove the second factor. Without 2FA, your account is only protected by your password — re-enable it as soon as possible.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => disableMutation.mutate(v))}
          className="flex flex-col gap-vt-4"
        >
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="totp-disable-pw">Current password</Label>
            <Input
              id="totp-disable-pw"
              type="password"
              autoComplete="current-password"
              autoFocus
              {...form.register('password')}
            />
            {form.formState.errors.password ? (
              <p className="text-vt-12 text-vt-alert" role="alert">
                {form.formState.errors.password.message}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={disableMutation.isPending} loadingText="Disabling…">
              Disable 2FA
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Regenerate recovery codes — password-gated; shows new codes ONCE.
// ──────────────────────────────────────────────────────────────────────
function TotpRegenerateDialog({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const [codes, setCodes] = useState<string[] | null>(null);

  const form = useForm<TotpPasswordForm>({
    resolver: zodResolver(TotpPasswordSchema),
    defaultValues: { password: '' }
  });

  useEffect(() => {
    if (open) {
      form.reset({ password: '' });
      setCodes(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const regenerateMutation = useMutation({
    mutationFn: (values: TotpPasswordForm) =>
      post<TotpRegenerateResponse, TotpPasswordForm>(
        '/superadmin/users/me/totp/regenerate-recovery-codes',
        values
      ),
    onSuccess: (res) => {
      setCodes(res.recovery_codes);
      toast.success('New recovery codes generated');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Regenerate recovery codes</DialogTitle>
          <DialogDescription>
            {codes
              ? 'Your previous recovery codes have been revoked. Save the new codes somewhere safe — you will not be able to view them again.'
              : 'Confirm your password. Your existing recovery codes will stop working once new ones are issued.'}
          </DialogDescription>
        </DialogHeader>
        {codes ? (
          <RecoveryCodesPanel codes={codes} onDone={() => onOpenChange(false)} />
        ) : (
          <form
            onSubmit={form.handleSubmit((v) => regenerateMutation.mutate(v))}
            className="flex flex-col gap-vt-4"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="totp-regen-pw">Current password</Label>
              <Input
                id="totp-regen-pw"
                type="password"
                autoComplete="current-password"
                autoFocus
                {...form.register('password')}
              />
              {form.formState.errors.password ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {form.formState.errors.password.message}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={regenerateMutation.isPending} loadingText="Generating…">
                <RefreshCw className="h-4 w-4" /> Generate new codes
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Shared "save your recovery codes" panel used by both setup + regenerate.
// ──────────────────────────────────────────────────────────────────────
function RecoveryCodesPanel({
  codes,
  onDone
}: {
  codes: string[];
  onDone: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob(
      [
        '# HoodDesk super-admin 2FA recovery codes\n',
        '# Each code may be used ONCE to sign in if your authenticator is unavailable.\n',
        '# Keep this file in a secure location (password manager, encrypted vault).\n',
        `# Generated: ${new Date().toISOString()}\n\n`,
        text,
        '\n'
      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hooddesk-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-vt-3">
      <div className="flex items-start gap-vt-2 rounded-vt-sm border border-vt-warn/30 bg-[color:var(--vt-warn-100)] px-vt-3 py-vt-2 text-vt-12 text-[color:var(--vt-warn)]">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Store these codes somewhere safe. You will not be able to view them again. Each code can be used once.
        </span>
      </div>
      <div className="grid grid-cols-2 gap-vt-2">
        {codes.map((c) => (
          <code
            key={c}
            className="select-all rounded-vt-sm border border-vt-border bg-vt-surface-2 px-vt-2 py-vt-2 text-center font-mono text-vt-13 text-vt-text"
          >
            {c}
          </code>
        ))}
      </div>
      <div className="flex items-center gap-vt-2">
        <Button type="button" variant="secondary" onClick={() => void handleCopy()} className="flex-1">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy all
        </Button>
        <Button type="button" variant="secondary" onClick={handleDownload} className="flex-1">
          <Download className="h-4 w-4" /> Download .txt
        </Button>
      </div>
      <DialogFooter>
        <Button type="button" onClick={onDone}>
          I've saved them
        </Button>
      </DialogFooter>
    </div>
  );
}

function TempPasswordDialog({
  view,
  onClose
}: {
  view: { email: string; password: string } | null;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const open = view !== null;

  const handleCopy = async (): Promise<void> => {
    if (!view) return;
    try {
      await navigator.clipboard.writeText(view.password);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setCopied(false);
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Temporary password issued</DialogTitle>
          <DialogDescription>
            Share this one-time password with <span className="font-mono">{view?.email}</span> through a trusted channel. It cannot be retrieved later — close this dialog only after you have copied it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-vt-2">
          <Label>Temporary password</Label>
          <div className="flex items-center gap-vt-2">
            <code className="flex-1 select-all break-all rounded-vt-sm border border-vt-border bg-vt-surface-2 px-vt-3 py-vt-2 font-mono text-vt-14 text-vt-text">
              {view?.password ?? ''}
            </code>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleCopy()}
              aria-label="Copy temporary password"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" /> Copy
                </>
              )}
            </Button>
          </div>
          <p className="text-vt-12 text-vt-text-soft">
            The user should change this password immediately after their first login.
          </p>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
